import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles, requiredRole }) => {
  const { state } = useAuth();
  const location = useLocation();

  // Helper function to check if user has a specific role
  // Handles both string and array role formats from JWT
  const userHasRole = (roleToCheck: string): boolean => {
    if (!state.user?.role) return false;
    const userRole = state.user.role;
    if (Array.isArray(userRole)) {
      return userRole.includes(roleToCheck as typeof userRole[number]);
    }
    return userRole === roleToCheck;
  };

  // Helper function to check if user has any of the specified roles
  const userHasAnyRole = (rolesToCheck: string[]): boolean => {
    return rolesToCheck.some(role => userHasRole(role));
  };

  // Show loading while checking auth
  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!state.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role permissions if roles array is specified
  if (roles && state.user && !userHasAnyRole(roles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check single required role
  if (requiredRole && state.user && !userHasRole(requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;